import Phaser from "phaser";

/**
 * Roamling / Knight Errand UI language. German is the project default; English
 * exists for the small fraction of participants whose study laptop ends up in
 * English. See `docs/superpowers/specs/2026-05-18-i18n-de-en-design.md`.
 */
export type Language = "de" | "en";

export type LanguageSource = "stored" | "auto-detect" | "default" | "launcher";

export interface LanguageChangeEvent {
  from: Language | null;
  to: Language;
  source: LanguageSource;
}

const STORAGE_KEY = "roamling.lang";

/**
 * Singleton language registry. `boot()` runs once at startup; scenes call
 * `getLanguage()` (via `t()`) on demand and subscribe to `onChange` if they
 * need to re-render on toggle (only the Launcher does).
 *
 * The first boot also persists the resolved language so subsequent boots
 * skip the auto-detect path — that way a participant who started in DE
 * on a German-locale laptop and then handed the laptop to the operator
 * doesn't lose their language preference if locale-detection ever fires
 * differently on a re-launch.
 */
class LanguageServiceImpl {
  private current: Language = "de";
  private booted = false;
  private readonly emitter = new Phaser.Events.EventEmitter();
  private bootListeners: ((ev: LanguageChangeEvent) => void)[] = [];
  private bootEvent?: LanguageChangeEvent;

  boot(): void {
    if (this.booted) return;
    const { language, source } = this.resolveInitial();
    this.current = language;
    if (source !== "stored") this.persist(language);
    this.booted = true;
    this.bootEvent = { from: null, to: language, source };
    for (const listener of this.bootListeners) listener(this.bootEvent);
    this.bootListeners = [];
  }

  /**
   * Subscribe to the one-shot boot event. If boot has already happened by the
   * time the listener attaches, fire immediately with the cached event so
   * late subscribers (TelemetryService instantiated in LauncherScene) still
   * see the initial resolution.
   */
  onBoot(listener: (ev: LanguageChangeEvent) => void): void {
    if (this.bootEvent) {
      listener(this.bootEvent);
      return;
    }
    this.bootListeners.push(listener);
  }

  getLanguage(): Language {
    return this.current;
  }

  setLanguage(next: Language, source: LanguageSource = "launcher"): void {
    if (next === this.current) return;
    const from = this.current;
    this.current = next;
    this.persist(next);
    const ev: LanguageChangeEvent = { from, to: next, source };
    this.emitter.emit("change", ev);
  }

  onChange(listener: (ev: LanguageChangeEvent) => void): () => void {
    this.emitter.on("change", listener);
    return () => this.emitter.off("change", listener);
  }

  private resolveInitial(): { language: Language; source: LanguageSource } {
    const stored = this.readStored();
    if (stored) return { language: stored, source: "stored" };
    if (this.browserPrefersDe()) return { language: "de", source: "auto-detect" };
    if (this.browserPrefersEn()) return { language: "en", source: "auto-detect" };
    return { language: "de", source: "default" };
  }

  private readStored(): Language | null {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw === "de" || raw === "en") return raw;
    } catch {
      // localStorage unavailable (privacy mode, SSR-like contexts) — fall
      // through to auto-detect.
    }
    return null;
  }

  private persist(lang: Language): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Same fallback as readStored — silent.
    }
  }

  private browserPrefersDe(): boolean {
    return this.browserLocale().startsWith("de");
  }

  private browserPrefersEn(): boolean {
    return this.browserLocale().startsWith("en");
  }

  private browserLocale(): string {
    try {
      return (navigator.language ?? "").toLowerCase();
    } catch {
      return "";
    }
  }
}

export const LanguageService = new LanguageServiceImpl();
